import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, FileSpreadsheet, Percent, Power, Trash2, Upload } from "lucide-react";
import { useApi, errMsg, CATEGORIES, GENDERS } from "@/lib/api";

const sel = "h-10 border rounded-full px-3 text-sm bg-white";
const Card = ({ title, hint, children, testid }) => (
  <section className="bg-white border p-6" data-testid={testid}>
    <h3 className="font-heading text-xl font-black uppercase tracking-tight">{title}</h3>
    <p className="mt-1 text-sm text-slate-500">{hint}</p>
    <div className="mt-5">{children}</div>
  </section>
);

const Filters = ({ brands, f, setF, prefix, allBrands = true }) => (
  <div className="flex flex-wrap gap-2">
    <select value={f.brand} onChange={(e) => setF({ ...f, brand: e.target.value })} className={sel} data-testid={`${prefix}-brand`}>
      {allBrands ? <option value="">Todas las marcas</option> : <option value="">Elige marca…</option>}
      {brands.map((b) => <option key={b.name} value={b.name}>{b.name} ({b.count})</option>)}
    </select>
    <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className={sel} data-testid={`${prefix}-category`}>
      <option value="">Todas las secciones</option>
      {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
    </select>
    <select value={f.gender} onChange={(e) => setF({ ...f, gender: e.target.value })} className={sel} data-testid={`${prefix}-gender`}>
      <option value="">Todos los géneros</option>
      {GENDERS.map((g) => <option key={g.id} value={g.id}>{g.label}</option>)}
    </select>
  </div>
);

const useUpload = (call) => async (e, endpoint, label, done) => {
  const file = e.target.files?.[0];
  if (!file) return;
  const fd = new FormData();
  fd.append("file", file);
  const t = toast.loading(`Subiendo ${label}…`);
  try { const { data } = await call("post", endpoint, fd); toast.success(done(data), { id: t }); }
  catch (err) { toast.error(errMsg(err, "Error al importar"), { id: t }); }
  e.target.value = "";
};

const StockCard = ({ upload }) => (
  <Card testid="prices-stock-card" title="Importar stock exacto" hint="Excel de ClassicGes. Solo actualiza los artículos de la tienda que vienen en el Excel (stock y precios); sus tallas que no aparecen quedan a 0. No crea artículos nuevos.">
    <label className="btn-primary !h-10 cursor-pointer inline-flex" data-testid="prices-stock-label">
      <Upload className="w-4 h-4 mr-2" /> Subir Excel de stock
      <input type="file" accept=".xlsx" className="hidden" data-testid="prices-stock-input"
        onChange={(e) => upload(e, "/admin/import-stock", "stock", (d) => `${d.updated} artículos actualizados · ${d.ignored} del Excel no están en la web`)} />
    </label>
  </Card>
);

const ExcelCard = ({ call, brands, upload }) => {
  const [f, setF] = useState({ brand: "", category: "", gender: "" });
  const download = async () => {
    try {
      const { data, headers } = await call("get", "/admin/prices/export", undefined, { params: f, responseType: "blob" });
      const name = /filename="([^"]+)"/.exec(headers["content-disposition"] || "")?.[1] || "precios.xlsx";
      const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(data), download: name });
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    } catch (e) { toast.error(errMsg(e, "No se ha podido descargar")); }
  };
  return (
    <Card testid="prices-excel-card" title="Precios por Excel" hint="Descarga los precios filtrados, cambia «Precio normal» y «Precio rebajado» (vacío = sin rebaja) y vuelve a subirlo. No toca fotos, descripciones ni stock.">
      <Filters brands={brands} f={f} setF={setF} prefix="prices-export" />
      <div className="mt-4 flex flex-wrap gap-3">
        <button className="btn-outline !h-10" onClick={download} data-testid="prices-export-button"><Download className="w-4 h-4 mr-2" /> Descargar precios</button>
        <label className="btn-primary !h-10 cursor-pointer inline-flex" data-testid="prices-import-label">
          <FileSpreadsheet className="w-4 h-4 mr-2" /> Subir precios retocados
          <input type="file" accept=".xlsx" className="hidden" data-testid="prices-import-input"
            onChange={(e) => upload(e, "/admin/prices/import", "precios", (d) => `${d.updated} precios actualizados${d.skipped ? ` · ${d.skipped} filas sin cambios` : ""}`)} />
        </label>
      </div>
    </Card>
  );
};

const arts = (n) => `${n} artículo${n === 1 ? "" : "s"}`;
const label = (c) => [c.brand, CATEGORIES.find((x) => x.id === c.category)?.label, GENDERS.find((x) => x.id === c.gender)?.label].filter(Boolean).join(" · ");

const CampaignRow = ({ c, onToggle, onDelete, busy }) => (
  <li className="flex flex-wrap items-center justify-between gap-3 py-3" data-testid={`campaign-row-${c.id}`}>
    <div>
      <p className="font-semibold">-{c.percent}% · {label(c)}</p>
      <p className="text-xs text-slate-500" data-testid={`campaign-status-${c.id}`}>{c.active ? `Activa en ${arts(c.count)}` : "Desactivada"}</p>
    </div>
    <div className="flex gap-2">
      <button disabled={busy} onClick={() => onToggle(c)} className={c.active ? "btn-outline !h-9" : "btn-primary !h-9"} data-testid={`campaign-toggle-${c.id}`}>
        <Power className="w-4 h-4 mr-2" /> {c.active ? "Desactivar" : "Activar"}
      </button>
      {!c.active && <button onClick={() => onDelete(c)} className="p-2 hover:text-brand" data-testid={`campaign-delete-${c.id}`}><Trash2 className="w-4 h-4" /></button>}
    </div>
  </li>
);

const CampaignCard = ({ call, brands }) => {
  const [f, setF] = useState({ brand: "", category: "", gender: "" });
  const [pct, setPct] = useState("");
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => call("get", "/admin/campaigns").then((r) => setList(r.data)), [call]);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    try { await call("post", "/admin/campaigns", { ...f, percent: Number(pct) }); toast.success("Campaña creada. Actívala cuando quieras."); setPct(""); load(); }
    catch (e) { toast.error(errMsg(e, "Elige marca y un % entre 1 y 89")); }
  };
  const toggle = async (c) => {
    setBusy(true);
    try { const { data } = await call("post", `/admin/campaigns/${c.id}/toggle`); toast.success(data.active ? `Descuento aplicado a ${arts(data.count)}` : `Precios restaurados en ${arts(data.count)}`); load(); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const remove = async (c) => {
    if (!window.confirm(`¿Borrar la campaña -${c.percent}% ${label(c)}?`)) return;
    try { await call("delete", `/admin/campaigns/${c.id}`); toast.success("Campaña borrada"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card testid="prices-campaign-card" title="Descuento % por marca" hint="Solo se aplica a artículos de la tienda sin rebaja. El precio anterior queda tachado. Al desactivar vuelven los precios de antes.">
      <Filters brands={brands} f={f} setF={setF} prefix="campaign" allBrands={false} />
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <div className="relative"><input type="number" min="1" max="89" value={pct} onChange={(e) => setPct(e.target.value)} placeholder="20" className="field !h-10 !w-28 pr-8" data-testid="campaign-percent" /><Percent className="w-4 h-4 absolute right-3 top-3 text-slate-400" /></div>
        <button className="btn-primary !h-10" onClick={create} disabled={!f.brand || !pct} data-testid="campaign-create-button">Crear campaña</button>
      </div>
      {list.length > 0 && <ul className="mt-6 divide-y border-t" data-testid="campaign-list">{list.map((c) => <CampaignRow key={c.id} c={c} busy={busy} onToggle={toggle} onDelete={remove} />)}</ul>}
    </Card>
  );
};

export const PricesTab = () => {
  const call = useApi();
  const [brands, setBrands] = useState([]);
  const upload = useUpload(call);
  useEffect(() => { call("get", "/admin/brands").then((r) => setBrands(r.data)); }, [call]);
  return (
    <div className="grid lg:grid-cols-2 gap-6 items-start" data-testid="admin-prices-tab">
      <StockCard upload={upload} />
      <ExcelCard call={call} brands={brands} upload={upload} />
      <div className="lg:col-span-2"><CampaignCard call={call} brands={brands} /></div>
    </div>
  );
};
